// Branded notification component for WHIPT
import React, { useEffect } from 'react';
import { CheckCircle, X } from 'lucide-react';

interface NotificationProps {
  message: string;
  type?: 'success' | 'error' | 'info';
  onClose: () => void;
  duration?: number;
}

export const WhiptNotification: React.FC<NotificationProps> = ({ 
  message, 
  type = 'success', 
  onClose,
  duration = 3000 
}) => {
  useEffect(() => {
    const timer = setTimeout(onClose, duration);
    return () => clearTimeout(timer);
  }, [duration, onClose]);

  const bgColors = {
    success: 'from-purple-600 to-purple-800',
    error: 'from-red-600 to-red-800',
    info: 'from-blue-600 to-blue-800',
  };

  return (
    <div className="fixed top-4 right-4 z-[99999] animate-slide-in-right">
      <div className={`bg-gradient-to-br ${bgColors[type]} rounded-lg shadow-2xl border border-white/10 p-4 min-w-[300px] max-w-md`}>
        <div className="flex items-start gap-3">
          {type === 'success' && (
            <CheckCircle className="w-6 h-6 text-green-300 flex-shrink-0 mt-0.5" />
          )}
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <span className="font-bold text-white text-sm">WHIPT</span>
              <span className="text-purple-200/60 text-xs">Transcription</span>
            </div>
            <p className="text-white text-sm leading-relaxed">
              {message}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-white/60 hover:text-white transition-colors flex-shrink-0"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

// Hook to show notifications
export const useWhiptNotification = () => {
  const [notification, setNotification] = React.useState<{
    message: string;
    type: 'success' | 'error' | 'info';
  } | null>(null);

  const showNotification = (message: string, type: 'success' | 'error' | 'info' = 'success') => {
    setNotification({ message, type });
  };

  const clearNotification = () => setNotification(null);

  const NotificationComponent = notification ? (
    <WhiptNotification
      message={notification.message}
      type={notification.type}
      onClose={clearNotification}
    />
  ) : null;

  return { showNotification, NotificationComponent };
};
